/* eslint-disable no-useless-escape */
import React, { useState, useEffect, ChangeEvent, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TextInput, DatePicker, PasswordErrorList, Button, Checkbox, ConfirmationDialog, FormErrorMessage } from "../../components";
import { formatPhoneNumber, passwordValidationList, validatePassword } from "../../utils";
import { calculateAge, isValidDate } from "../../utils/date";
import { Phone_Fax_Regex, PrimaryContactEmail } from "../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  changeUserType,
  clearSessionStorage,
  getSessionStorage,
  sendEmailVerificationCode,
  sendPhoneVerificationCode,
  updateRequestFormData,
  updateSelectedOffice,
  verifyEmailCode,
  verifyPhoneCode,
} from "../../redux/slices/OnBoardingSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { FormType, ToastVariants } from "../../models";
import { emailExists } from "../../redux/slices/CurrentUserSlice";
import TermsAndConditions from "../Guest/TermsAndCondition";
import PrivacyPolicy from "../Guest/PrivacyPolicy";
import { Autocomplete, InputAdornment, TextField } from "@mui/material";
import { NoDataFound, noData } from "../Auth/PatientSignupPage";
import { Office } from "../../models/office.model";
import { listOfficeData } from "../../redux/slices/RequestSlice";
import "../../assets/css/styles.css";
import SearchIcon from "../../assets/images/search-icon.svg";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import OtpComponent from "../../components/OtpComponent";
import { UserType } from "../../services/user.service";
import { createUser } from "../../redux/slices/UserSlice";
import { AuthContext } from "../../contexts/AuthContext/AuthContext";
import { isOhipValid } from "../../utils/Onboarding/validation";

import OhipNumberField from "./OhipNumberField";

const RequestForm = ({ formType = FormType.CreateAccount, patientEmail = "" }: { formType?: FormType; patientEmail?: any }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const auth = useContext(AuthContext);
  const { pathname } = useLocation();
  const isCreateAccount = pathname === "/create-account";
  const officeId = useSelector((state: RootState) => state.onBoarding.selectedOffice?.officeId);
  const formDataRedux = useSelector((state: RootState) => state.onBoarding.requestFormData);
  const { pending } = useSelector((state: RootState) => state.onBoarding);
  const { loading } = useSelector((state: RootState) => state.currentUser);
  const formData = formDataRedux;
  const email = formData.filter(v => v.id === "email")[0].value;
  const mobile = formData.filter(v => v.id === "phone")[0].value;
  const [pwdValidations, setPwdValidations] = useState(passwordValidationList);
  const [errorInitiated, setErrorInitiated] = useState(false);
  const [touchedPwd, setTouchedPwd] = useState(false);
  const passwordFlds = ["password", "confirmPassword"];
  const [dateError, setDateError] = useState(true);
  const [tcAccepted, setTcAccepted] = useState(formData[formData.findIndex(v => v.id === "requestFormTerms")].value === "true");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingEmailError, setSendingEmailError] = useState(false);
  const [emailExistError, setEmailExistError] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [openPrivacyPolicy, setOpenPrivacyPolicy] = React.useState(false);
  const [showEmailMfa, setShowEmailMfa] = useState(false);
  const [showMobileMfa, setShowMobileMfa] = useState(false);
  const [showError, setShowError] = useState(false);
  const [loadingOtp, setLoading] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [showMethod, setShowMethod] = useState(false);
  const [dobErrorMsg, setDobErrorMsg] = useState("Date of birth is required.");
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const excludedFields = [
    "details",
    "recipientName",
    "recipientContact",
    "recipientConsent",
    "fileIds",
    "password",
    "confirmPassword",
    "requestFormTerms",
  ];
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOffice, setSearchOffice] = useState<Office | null>(null);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClickOpenPrivacyPolicy = () => {
    setOpenPrivacyPolicy(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleClosePrivacyPolicy = () => {
    setOpenPrivacyPolicy(false);
  };

  const resetPasswordFields = (type: FormType) => {
    const updatedFields = formData.map(it => {
      const itCpy = { ...it };
      if (["password", "confirmPassword"].includes(it.id)) {
        itCpy.value = "";
        itCpy.isValid = type === FormType.ContinueGuest ? true : false;
      }
      setPwdValidations(passwordValidationList);
      return itCpy;
    });
    dispatch(updateRequestFormData(updatedFields));
  };

  const validateField = (name: string, value: string): { valid: boolean; errorMsg?: string } => {
    let valid = false;
    let errorMsg = "";

    switch (name) {
      case "firstName": {
        const trimmed = value.trim();
        if (!trimmed) {
          errorMsg = "First name is required.";
        } else if (trimmed.length < 2) {
          errorMsg = "First name must be at least 2 characters.";
          valid = false;
        } else if (trimmed.length > 25) {
          errorMsg = "First name must be at most 25 characters.";
          valid = false;
        } else if (!/^[a-zA-Z\s',.-]+$/.test(trimmed)) {
          errorMsg = "First name must contain only letters.";
          valid = false;
        } else {
          valid = true;
        }
        break;
      }
      case "lastName": {
        const trimmed = value.trim();
        if (!trimmed) {
          errorMsg = "Last name is required.";
        } else if (trimmed.length < 2) {
          errorMsg = "Last name must be at least 2 characters.";
          valid = false;
        } else if (trimmed.length > 25) {
          errorMsg = "Last name must be at most 25 characters.";
          valid = false;
        } else if (!/^[a-zA-Z\s]+$/.test(trimmed)) {
          errorMsg = "Last name must contain only letters.";
          valid = false;
        } else {
          valid = true;
        }
        break;
      }
      case "email": {
        const trimmed = value.trim();
        if (!trimmed) {
          errorMsg = "Email is required.";
        } else if (/\s/.test(trimmed)) {
          errorMsg = "Please enter a valid email address.";
          valid = false;
        } else if ((trimmed.match(/@/g) || []).length !== 1) {
          errorMsg = "Please enter a valid email address.";
          valid = false;
        } else if (!PrimaryContactEmail.test(trimmed)) {
          errorMsg = "Please enter a valid email address.";
          valid = false;
        } else {
          valid = true;
        }
        break;
      }
      case "phone": {
        const trimmed = value.trim();
        if (!trimmed) {
          errorMsg = "Please provide a mobile number.";
        } else {
          // Remove formatting characters to check digit count
          const digitsOnly = trimmed.replace(/\D/g, "");
          if (digitsOnly.length !== 10) {
            errorMsg = "Please enter a valid 10-digit mobile number.";
            valid = false;
          } else if (!Phone_Fax_Regex.test(trimmed)) {
            errorMsg = "Please enter a valid 10-digit mobile number.";
            valid = false;
          } else {
            valid = true;
          }
        }
        break;
      }
      case "ohip": {
        const trimmed = value.trim();
        if (!trimmed) {
          errorMsg = "Valid Health Card Number required";
        } else {
          // Remove all non-numeric characters
          const digitsOnly = trimmed.replace(/\D/g, "");
          if (digitsOnly.length !== 10) {
            errorMsg = "Valid Health Card Number required";
            valid = false;
          } else if (!/^\d{10}$/.test(digitsOnly)) {
            errorMsg = "Valid Health Card Number required";
            valid = false;
          } else {
            valid = true;
          }
        }
        break;
      }
      case "birthday":
        valid = !dateError;
        errorMsg = dobErrorMsg;
        break;
      case "password":
        valid = !!value.trim();
        break;
      case "confirmPassword":
        valid = value === formData.find(it => it.id === "password")?.value;
        break;
      case "details":
        valid = !!value.trim();
        break;
      default:
        break;
    }

    // Update field error message
    if (errorMsg) {
      setFieldErrors(prev => ({ ...prev, [name]: errorMsg }));
    } else {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    return { valid, errorMsg: errorMsg || undefined };
  };

  useEffect(() => {
    if (!patientEmail || typeof patientEmail !== "string" || !patientEmail.trim()) return;
    const emailIndex = formDataRedux.findIndex(v => v.id === "email");
    if (emailIndex === -1) return;
    const currentEmailValue = (formDataRedux[emailIndex].value || "").trim();
    if (currentEmailValue !== "") return;
    const trimmedPatientEmail = patientEmail.trim();
    // Validate the email field to set isValid and errorMesage
    const validationResult = validateField("email", trimmedPatientEmail);
    const newFormData = [
      ...formDataRedux.slice(0, emailIndex),
      {
        ...formDataRedux[emailIndex],
        value: trimmedPatientEmail,
        isValid: validationResult.valid,
        errorMesage: validationResult.errorMsg || "",
      },
      ...formDataRedux.slice(emailIndex + 1),
    ];
    dispatch(updateRequestFormData(newFormData));
  }, [patientEmail, formDataRedux, dispatch]);

  const hasValidBaseFields = () => formData.map(v => v.isValid || excludedFields.includes(v.id)).every(i => i);
  const hasAcceptedTermsAndConditions = () => !!tcAccepted;
  const hasValidBirthday = () => !dateError;
  const hasValidPasswordFields = () => pwdValidations.map(p => p.isValid).every(i => i);
  const validateContinueGuestForm = () => hasValidBaseFields() && hasAcceptedTermsAndConditions() && hasValidBirthday();

  const validateForm = () => {
    if (formType === FormType.ContinueGuest) {
      return validateContinueGuestForm();
    } else if (formType === FormType.CreateAccount || formType === FormType.PatientSignUp) {
      // TODO: Improve the understandability of this conditional
      if (isCreateAccount && !searchOffice) {
        return false;
      }
      return validateContinueGuestForm() && hasValidPasswordFields();
    }
  };

  const isSubmitDisabled = () => !validateForm() && errorInitiated;

  const sendOTPToMobile = async (resend?: boolean) => {
    setLoading(true);
    const res = await dispatch(sendPhoneVerificationCode({ phone: formData.filter(v => v.id === "phone")[0].value }));
    if (res?.payload) {
      if (resend) {
        toast?.openToast("A new code has been sent to your mobile number.", 2000, ToastVariants.Success);
      }
      setShowMobileMfa(true);
    } else {
      toast?.errorToast("Please enter valid mobile number");
    }
    setLoading(false);
  };

  const verifyMobile = async () => {
    await verifyEmail();
    setShowError(false);
    setLoading(true);
    setShowMobileMfa(false);
    setOtpInput("");
  };

  const sendOTPToEmail = async (resend?: boolean) => {
    setLoading(true);
    const res = await dispatch(
      sendEmailVerificationCode({
        email: formData.filter(v => v.id === "email")[0].value,
        firstName: formData.filter(v => v.id === "firstName")[0].value,
        lastName: formData.filter(v => v.id === "lastName")[0].value,
      })
    );
    if (res.payload) {
      if (resend) {
        toast?.openToast("A new code has been sent to your email.", 2000, ToastVariants.Success);
      }
      setShowError(false);
    } else {
      setShowError(true);
    }
    setLoading(false);
  };

  const onClickSave = () => {
    // Enforce 18+ validation on submit
    const birthdayValue = formData.find(v => v.id === "birthday")?.value as string;
    if (birthdayValue) {
      if (isValidDate(birthdayValue)) {
        const age = calculateAge(birthdayValue);
        if (age < 18) {
          setDobErrorMsg("You must be at least 18 years old to create an account.");
          setDateError(true);
          setErrorInitiated(true);
          return;
        }
      }
    }
    if (validateForm()) {
      if (formType === FormType.ContinueGuest) {
        setSendingEmail(true);
        setSendingEmailError(false);
        dispatch(
          sendEmailVerificationCode({
            email: formData.filter(v => v.id === "email")[0].value,
            firstName: formData.filter(v => v.id === "firstName")[0].value,
            lastName: formData.filter(v => v.id === "lastName")[0].value,
          })
        ).then(action => {
          setSendingEmail(false);
          if (action.payload) {
            navigate("/onboarding/verify-email");
          } else {
            setSendingEmailError(true);
          }
        });
      } else {
        setEmailExistError(false);
        const email = formData.filter(v => v.id === "email")[0].value;
        dispatch(emailExists({ email, officeId })).then(action => {
          const myPayload = action?.payload as any;
          if (myPayload?.data && myPayload?.data?.message) {
            const errorMsg = myPayload?.data.message ? myPayload?.data.message : "Something went wrong.";
            toast?.openToast(errorMsg, 6000, ToastVariants.Error);
            return;
          }
          if (action.payload === true) {
            setEmailExistError(true);
          } else {
            setShowMethod(true);
            // remove sendOTPToMobile this because we need tp open verifaction popup
            // sendOTPToMobile();
          }
        });
      }
    }
  };

  const verifyEmail = async () => {
    try {
      setLoading(true);
      if (officeId) {
        const formType = localStorage.getItem("formType");
        if (formType === FormType.CreateAccount || formType === FormType.PatientSignUp) {
          const baseUser = {
            officeId: officeId,
            firstName: formData[0].value,
            lastName: formData[1].value,
            email: formData[2].value,
            dateOfBirth: formData[3].value,
            phone: formData[4].value,
            password: formData[6].value,
            userType: UserType.Patient,
            code: otpInput,
            ohipNumber: formData[5].value,
            acceptedTerms: formData[13].value === "true" ? true : false,
          };
          const user = showMobileMfa
            ? { ...baseUser } // omit `code`
            : { ...baseUser, code: otpInput };
          const res = await dispatch(createUser(user));
          if (res.payload) {
            if (typeof res.payload === "string") {
              if (res.payload === "Invalid code") {
                toast?.openToast("Invalid code, please try again", 2000, ToastVariants.Error);
                setShowError(true);
              } else {
                toast?.openToast(res.payload, 2000, ToastVariants.Error);
              }
            } else {
              setShowError(false);
              // login here
              const Login = await auth?.signin({
                email: user.email.trim(),
                password: user.password.trim(),
                isPatient: true,
              });
              if (Login) {
                dispatch(clearSessionStorage());
                localStorage.removeItem("formType");
                navigate(`/patients/${res.payload.userId}/dashboard`);
              }
            }
          } else {
            toast?.openToast("Something went wrong. Try again!", 2000, ToastVariants.Error);
          }
          setLoading(false);
        } else {
          dispatch(verifyEmailCode({ email: email, code: otpInput })).then(action => {
            setLoading(false);
            if (action.payload) {
              navigate("/onboarding/submit-request");
            } else {
              toast?.openToast("Invalid code, please try again", 2000, ToastVariants.Error);
            }
          });
        }
      }
    } catch (err) {}
  };

  const handleInputChange = (e: any | string) => {
    let value = "";
    value = e.target.value;
    let cursorPosition = e.target.selectionEnd;
    switch (e.currentTarget.name) {
      case "phone":
        value = formatPhoneNumber(e.currentTarget.value);
        if (cursorPosition === 4 || cursorPosition === 8) {
          cursorPosition++;
        }
        break;
      case "birthday":
        const dateVals = value.split("-");
        value = e.currentTarget.value;
        if (parseInt(dateVals[0]) > 9999) {
          dateVals[0] = dateVals[0].substring(0, value.length - 1);
          value = dateVals.join("-");
        }
        break;
      case "confirmPassword":
        const passwordData = formData.filter(e => e.id === "password");
        const isValid = passwordData[0].value === value;
        const validationErrors = [...pwdValidations];
        validationErrors[5] = { ...validationErrors[5], isValid };
        if (value.length > 0) {
          setTouchedPwd(true);
        } else {
          setTouchedPwd(false);
        }
        setPwdValidations(validationErrors);
        break;
      case "password":
        const newValidations = pwdValidations.map(item => {
          const isValid = validatePassword(item.id, value);

          return { ...item, isValid };
        });
        if (value.length > 0) {
          setTouchedPwd(true);
        } else {
          setTouchedPwd(false);
        }
        setPwdValidations(newValidations);
        break;
      default:
        break;
    }

    const validationResult = validateField(e.currentTarget.name, value);
    const valid = validationResult.valid;

    // Update error message in form data if needed
    let updatedErrorMsg = "";
    if (!valid && validationResult.errorMsg) {
      updatedErrorMsg = validationResult.errorMsg;
    }

    //getting state
    const newFromState = formData.map(item => {
      if (item.id === e.currentTarget.name) {
        return {
          ...item,
          value,
          isValid: valid,
          cursorPosition,
          errorMesage: updatedErrorMsg || item.errorMesage,
        };
      } else {
        return { ...item, cursorPosition };
      }
    });
    dispatch(updateRequestFormData(newFromState));
  };

  const handleBlur = async (id: string, value: string) => {
    const index = formData.findIndex(item => item.id === id);
    if (index !== -1) {
      // Validate field on blur
      const validationResult = validateField(id, value);

      // Check email uniqueness if it's the email field
      if (id === "email" && validationResult.valid && value.trim()) {
        try {
          const emailCheckResult = await dispatch(emailExists({ email: value.trim(), officeId: officeId }));
          if (emailCheckResult.payload === true) {
            validationResult.valid = false;
            validationResult.errorMsg = "Email already exits";
            setFieldErrors(prev => ({ ...prev, [id]: "Email already exits" }));
          }
        } catch (error) {
          // Silently fail - don't block user if API call fails
        }
      }

      const newFormData = [
        ...formData.slice(0, index),
        {
          ...formData[index],
          value,
          isValid: validationResult.valid,
          errorMesage: validationResult.errorMsg || formData[index].errorMesage,
        },
        ...formData.slice(index + 1),
      ];
      dispatch(updateRequestFormData(newFormData));
    }
  };

  const handleTermsCheckbox = () => {
    setTcAccepted(!tcAccepted);
    dispatch(
      updateRequestFormData([
        ...formDataRedux.slice(
          0,
          formDataRedux.findIndex(v => v.id === "requestFormTerms")
        ),
        { ...formDataRedux[formDataRedux.findIndex(v => v.id === "requestFormTerms")], value: !tcAccepted ? "true" : "false" },
        ...formDataRedux.slice(formDataRedux.findIndex(v => v.id === "requestFormTerms") + 1),
      ])
    );
  };

  const changeDate = (dateVal: string) => {
    if (!dateVal) {
      setDateError(true);
      setDobErrorMsg("Date of birth is required.");
      dispatch(updateRequestFormData([...formDataRedux.slice(0, 3), { ...formDataRedux[3], value: "", isValid: false }, ...formDataRedux.slice(4)]));
      return;
    }

    // Validate date format (YYYY-MM-DD from DatePicker)
    if (!isValidDate(dateVal)) {
      setDateError(true);
      setDobErrorMsg("Please enter a valid date of birth.");
      dispatch(
        updateRequestFormData([
          ...formDataRedux.slice(0, 3),
          { ...formDataRedux[3], value: dateVal?.toString(), isValid: false },
          ...formDataRedux.slice(4),
        ])
      );
      return;
    }

    // Check if date is in the future
    const dateObj = new Date(dateVal);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateObj > today) {
      setDateError(true);
      setDobErrorMsg("Please enter a valid date of birth.");
      dispatch(
        updateRequestFormData([
          ...formDataRedux.slice(0, 3),
          { ...formDataRedux[3], value: dateVal?.toString(), isValid: false },
          ...formDataRedux.slice(4),
        ])
      );
      return;
    }

    // 18+ age validation
    const age = calculateAge(dateVal);
    if (age < 18) {
      setDateError(true);
      setDobErrorMsg("You must be at least 18 years old to create an account.");
      dispatch(
        updateRequestFormData([
          ...formDataRedux.slice(0, 3),
          { ...formDataRedux[3], value: dateVal?.toString(), isValid: false },
          ...formDataRedux.slice(4),
        ])
      );
      return;
    }

    // Valid date
    setDateError(false);
    setDobErrorMsg("Date of birth is required.");
    dispatch(
      updateRequestFormData([
        ...formDataRedux.slice(0, 3),
        { ...formDataRedux[3], value: dateVal?.toString(), isValid: true },
        ...formDataRedux.slice(4),
      ])
    );
  };

  const OHIP_NUMBER_FORM_DATA_ITEM_INDEX = 5;

  const handleChangeOhipNumber = (val: string) => {
    const ohipNumberFormDataItem = formDataRedux[OHIP_NUMBER_FORM_DATA_ITEM_INDEX];

    dispatch(
      updateRequestFormData([
        ...formDataRedux.slice(0, OHIP_NUMBER_FORM_DATA_ITEM_INDEX),
        {
          ...ohipNumberFormDataItem,
          value: val,
          isValid: isOhipValid(val),
        },
        ...formDataRedux.slice(OHIP_NUMBER_FORM_DATA_ITEM_INDEX + 1),
      ])
    );
  };

  const { officeList } = useSelector((state: RootState) => state.request);

  const getFilteredData = (): Office[] | [] => {
    if (searchTerm.length <= 2) return [];

    const matchedFilteredData: Office[] = [];

    officeList.data?.content.forEach(el => {
      // discarding the loop if no primary care providers are there

      if (!el.primaryCareProviders || el.primaryCareProviders?.length === 0) {
        matchedFilteredData.push(el);
        return;
      }

      //searching for all the pcp which includes the search term in first name or in last name

      const drNameIncluded = el.primaryCareProviders?.filter(pcp => {
        const fullName = `${pcp.firstName.toLowerCase()} ${pcp?.lastName?.toLowerCase()}`;

        const containsFirstName = pcp.firstName.toLowerCase().includes(searchTerm.toLowerCase());
        const containsLastName = pcp?.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
        const containsFullName = fullName.includes(searchTerm.toLowerCase());

        return containsFirstName || containsLastName || containsFullName;
      });

      //pushing this result only when of the doctor name is matching with search term

      const data = { ...el, primaryCareProviders: drNameIncluded };
      matchedFilteredData.push(data);
    });

    return matchedFilteredData;
  };

  const onOptionSelect = (officeData: Office) => {
    setSearchOffice(officeData);
    dispatch(updateSelectedOffice(officeData));
  };

  useEffect(() => {
    dispatch(getSessionStorage());
  }, []);

  useEffect(() => {
    if (searchTerm.length !== 0) {
      if (searchTerm.length > 2) {
        dispatch(listOfficeData({ searchTerm, page: 0, sortType: "", size: 100 }));
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem("formType", formType);
    dispatch(changeUserType(formType));
    resetPasswordFields(formType);
  }, [formType]);

  useEffect(() => {
    if (!officeId && !isCreateAccount) {
      navigate("/");
    }
  }, [officeId, isCreateAccount]);

  const verifyByEmail = () => {
    setShowMethod(false);
    setShowMobileMfa(false);
    setShowEmailMfa(true);
    sendOTPToEmail();
    localStorage.removeItem("signPatientEmail");
  };
  const verifyByPhone = () => {
    sendOTPToMobile();
    setShowMethod(false);
    localStorage.removeItem("signPatientEmail");
  };
  const handleBack = () => {
    if (showEmailMfa) {
      setShowEmailMfa(false);
    } else {
      setShowMobileMfa(false);
    }
  };
  return (
    <>
      <main>
        <div className="flex justify-between w-full">
          {!showEmailMfa && !showMobileMfa ? (
            <div className="w-full max-w-2xl mx-auto md:px-0">
              {/* <h3 className="text-3xl font-light mb-2">Contact Details</h3> */}

              {formType === FormType.ContinueGuest ? (
                <p className="text-base mb-5">
                  Personal information is required to confirm your identity. You can also{" "}
                  <Link to="/onboarding/create-account" className="text-docsigna-purple-dark">
                    create an account
                  </Link>{" "}
                  to save your information for future requests.
                </p>
              ) : formType === FormType.CreateAccount ? (
                <p className="text-base font-medium mb-2">Please enter your details below to create your account</p>
              ) : null}
              {sendingEmailError && <FormErrorMessage text="An error has occurred, please try again" />}
              {isCreateAccount ? (
                <>
                  <section className="search mb-5">
                    {!searchOffice ? (
                      <div>
                        <label className="text-sm text-docsigna-blue-dark font-medium block w-full">Select your doctor’s office below:</label>
                        <Autocomplete
                          freeSolo={true}
                          className="rounded-md border border-docsigna-blue-dark"
                          id="search-office-box"
                          options={getFilteredData().length > 0 ? getFilteredData() : noData}
                          onInputChange={(e: React.SyntheticEvent<Element, Event>, value) => {
                            setSearchTerm(value ? value : "");
                          }}
                          disableClearable
                          openOnFocus
                          open={searchTerm.length > 2}
                          includeInputInList={true}
                          filterOptions={x => x}
                          componentsProps={{
                            paper: {
                              classes: "absolute left-0 top-13 w-full rounded-md border border-gray-300 bg-white",
                            },
                          }}
                          ListboxProps={{
                            className: "absolute left-0 top-13 w-full rounded-md border border-gray-300 bg-white",
                          }}
                          renderOption={(props, option) => {
                            const el = option as Office;
                            return (
                              //using random number to avoid duplicate key as officeId
                              <span {...props} className="hover:bg-docsigna-purple-light" key={el.officeId + Math.random().toString()}>
                                {getFilteredData().length > 0 ? (
                                  <div
                                    className="search-row p-3 w-full border-b border-gray-300 hover:bg-docsigna-purple-light cursor-pointer"
                                    onClick={() => onOptionSelect(el)}
                                  >
                                    <p className="text-lg font-semibold mb-1">{el.name}</p>
                                    <>
                                      {el.primaryCareProviders?.map((pcp, ind) => {
                                        return <p key={ind} className="text-sm font-semibold mb-1">{` Dr. ${pcp.firstName} ${pcp?.lastName}`}</p>;
                                      })}
                                    </>
                                    <p className="text-sm"> {[el.street, el.street1, el.city, el.state].filter(Boolean).join(", ")} </p>
                                  </div>
                                ) : (
                                  <NoDataFound />
                                )}
                              </span>
                            );
                          }}
                          renderInput={params => {
                            // let newInputProps = getFilteredData().length ? params.InputProps : {}
                            const newInputProps = params.InputProps;
                            return (
                              <TextField
                                {...params}
                                placeholder="Search by doctor’s name or address"
                                type="search"
                                InputProps={{
                                  ...newInputProps,
                                  startAdornment: (
                                    <InputAdornment position="start" className="">
                                      <img src={SearchIcon} alt="search" width={18} height={18} />
                                    </InputAdornment>
                                  ),
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    outline: "none",
                                    margin: "0px",
                                    boxShadow: "none",
                                    padding: 0,
                                    border: 0,
                                  },
                                }}
                              />
                            );
                          }}
                        />
                      </div>
                    ) : null}
                    {searchOffice ? (
                      <>
                        <h2 className="font-bold text-lg mb-0">{searchOffice?.name}</h2>
                        <p className="mb-2 font-medium">
                          {[searchOffice?.street, searchOffice?.street1, searchOffice?.city, searchOffice?.state, searchOffice?.zip]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      </>
                    ) : errorInitiated ? (
                      <FormErrorMessage text="Please select office" />
                    ) : null}
                  </section>
                  <h2 className="font-bold mb-0 capitalize">Personal information</h2>
                </>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pb-1">
                {formData.map((item, index, array) => {
                  if (["password", "confirmPassword"].includes(item.id) && formType === FormType.ContinueGuest) {
                    return null;
                  }
                  if (item.id === "birthday") {
                    return (
                      <div className="py-0.5 flex" key={item.id}>
                        <div className="w-full">
                          <DatePicker
                            label={item.label}
                            isRequired={item.required}
                            name={item.id}
                            placeholder={item.placeholder}
                            value={item.value}
                            isError={dateError && errorInitiated}
                            onChange={val => changeDate(val)}
                            errorMsg={dobErrorMsg}
                            onError={(e: boolean) => setDateError(e)}
                            showError={errorInitiated}
                            isTransparent={true}
                            onlyIconOpens={true}
                          />
                        </div>
                      </div>
                    );
                  } else if (item.id === "password" || item.id === "confirmPassword") {
                    return (
                      <div className="py-0.5" key={item.id}>
                        <TextInput
                          isError={!item.isValid && errorInitiated}
                          isRequired={item.required}
                          isPassword={passwordFlds.includes(item.id)}
                          name={item.id}
                          label={item.label}
                          value={String(item.value)}
                          placeholder={item.placeholder}
                          errorMsg={item.errorMesage}
                          onChangeFunc={e => handleInputChange(e)}
                          isTransparent={true}
                        />
                      </div>
                    );
                  } else if (item.id === "ohip") {
                    // Use dynamic error message from fieldErrors if available
                    const ohipErrorMessage = fieldErrors[item.id] || item.errorMesage;
                    return (
                      <OhipNumberField
                        key={item.id}
                        field={{ ...item, errorMesage: ohipErrorMessage }}
                        onChange={handleChangeOhipNumber}
                        isError={!item.isValid && errorInitiated}
                        isTransparent={true}
                      />
                    );
                  } else if (!excludedFields.includes(item.id)) {
                    // Use dynamic error message from fieldErrors if available, otherwise use item.errorMesage
                    const errorMessage = fieldErrors[item.id] || item.errorMesage;
                    return (
                      <div className="py-0.5" key={item.id}>
                        <TextInput
                          isError={!item.isValid && errorInitiated}
                          isRequired={item.required}
                          isPassword={passwordFlds.includes(item.id)}
                          name={item.id}
                          label={`${item.label}`}
                          value={String(item.value)}
                          placeholder={item.placeholder}
                          errorMsg={errorMessage}
                          onChangeFunc={e => handleInputChange(e)}
                          onBlur={e => handleBlur(item.id, e.currentTarget.value.trim())}
                          isTransparent={true}
                        />
                      </div>
                    );
                  } else {
                    return null;
                  }
                })}
              </div>
              <div className="py-0.5 mb-2">
                <label className="flex justify-start items-center">
                  <Checkbox checked={tcAccepted} onChange={() => handleTermsCheckbox()} label="" />
                  <span className="text-sm font-medium ml-2">
                    * I agree to Docnote&apos;s{" "}
                    <span className="text-sm text-docsigna-blue-light" onClick={() => handleClickOpen()}>
                      Terms and Conditions
                    </span>{" "}
                    and{" "}
                    <span className="text-sm text-docsigna-blue-light" onClick={() => handleClickOpenPrivacyPolicy()}>
                      Privacy Policy
                    </span>
                  </span>
                </label>
                {!tcAccepted && errorInitiated ? <FormErrorMessage className="ml-6" text="You must agree to Docnote terms to continue" /> : null}
              </div>
              {emailExistError ? (
                <FormErrorMessage text="An account already exists with this email address, please sign in to create your request." />
              ) : null}
              <div className="flex justify-center">
                {(touchedPwd && formType !== FormType.ContinueGuest) || (errorInitiated && formType !== FormType.ContinueGuest) ? (
                  <div>
                    {pwdValidations.map(item => {
                      return (
                        <div key={item.id} className="my-1 ml-1">
                          <PasswordErrorList isValid={item.isValid} message={item.msg} />
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="flex justify-center mt-3" onClick={() => setErrorInitiated(true)}>
                <Button
                  onClick={() => {
                    onClickSave();
                  }}
                  className="pointer"
                  text={"Create Account"}
                  width="full"
                  disabled={isSubmitDisabled()}
                  variant={sendingEmail || pending || loading ? "loading" : "Primary"}
                />
              </div>
            </div>
          ) : showEmailMfa || showMobileMfa ? (
            <div className="flex flex-col flex-wrap space-y-2 mt-3 w-full">
              <OtpComponent
                value={showMobileMfa ? mobile : email}
                isMobile={showMobileMfa}
                otpInput={otpInput}
                setOtpInput={setOtpInput}
                loading={loadingOtp}
                showError={!loading && showError}
                submit={() => (showMobileMfa ? verifyMobile() : verifyEmail())}
                resend={() => (showMobileMfa ? sendOTPToMobile(true) : sendOTPToEmail(true))}
                onBack={handleBack}
              />
            </div>
          ) : null}
        </div>
        <ConfirmationDialog
          maxWidth="lg"
          successButtonText=""
          failureButtonText="Close"
          open={open}
          description={<TermsAndConditions showCheckbox={false} />}
          handleSuccess={() => {}}
          handleFailure={handleClose}
        />
        <ConfirmationDialog
          maxWidth="lg"
          successButtonText=""
          failureButtonText="Close"
          open={openPrivacyPolicy}
          description={<PrivacyPolicy />}
          handleSuccess={() => {}}
          handleFailure={handleClosePrivacyPolicy}
        />
        <ConfirmationDialog
          open={showMethod}
          description={"Choose a method to verify your account."}
          descriptionPosition={true}
          actionButtonPosition="Center"
          successButtonText={"Mobile Phone Number"}
          failureButtonText={"Email"}
          handleSuccess={verifyByPhone}
          handleFailure={verifyByEmail}
          modalVerify={true}
          canClickOutSide={false}
        />
      </main>
    </>
  );
};
export default RequestForm;
